import Vue from 'vue'
import Vuex, { Store } from 'vuex'
import createPersistedState from 'vuex-persistedstate'

Vue.use(Vuex);

//2025-05-08 jazz
//mae added 2025-04-28
const generateSortFn = (sheetItems) => {
    return sheetItems.sort((a, b) => {
        if (a.sortno !== b.sortno) {
            return a.sortno - b.sortno;
        }

        const matchA = a.items.match(/\d+/);
        const matchB = b.items.match(/\d+/);

        const numA = matchA ? parseInt(matchA[0]) : 0;
        const numB = matchB ? parseInt(matchB[0]) : 0;
        return numA - numB;
    });
};

//2026-10-07 jazz, debounced persistence.
//
//vuex-persistedstate subscribes to EVERY mutation and, by default, runs
//`storage.setItem(key, JSON.stringify(state))` on each one. This store is persisted whole (no
//`paths` / `reducer`), so that state includes all three sheet trees (GaibuList / SetsubiSheet /
//BasicSpecs), ShiyouData and Master - megabytes. Every mutation therefore paid a full-store
//JSON.stringify plus a SYNCHRONOUS localStorage write on the main thread, and a single user
//action fires many mutations (selecting one texture dispatches 6+ on its own, before the
//per-row loops in SetPreselected / the triggered-texture block are counted). That is what made
//clicking an item or a texture feel slow.
//
//Overriding `setState` (an official option - see the plugin's index.d.ts) lets a burst of
//mutations collapse into ONE stringify and ONE write once the burst settles. Nothing about WHAT
//is persisted changes, only when - and the listeners below flush synchronously before the tab
//can go away, so a reload still restores the same state.
const __persistQueue = (() => {
    let pending = null //only the newest snapshot matters; earlier ones are superseded
    let timer   = null

    const flush = () => {
        if(timer){
            clearTimeout(timer)
            timer = null
        }

        if(!pending) return

        const { key, state, storage } = pending
        pending = null

        try{
            storage.setItem(key, JSON.stringify(state))
        }catch(e){
            //a failed write must never break the mutation that triggered it
            console.error('[SRS] persisting state to localStorage failed', e)
        }
    }

    if(typeof window !== 'undefined'){
        //flush the tail of a burst before the page goes away, so nothing is lost
        window.addEventListener('beforeunload', flush)
        window.addEventListener('pagehide', flush)
        document.addEventListener('visibilitychange', () => {
            if(document.visibilityState === 'hidden') flush()
        })
    }

    return {
        //with no `paths` configured the plugin's default reducer hands us the live store state
        //object, so stringifying it at flush time serialises the newest values
        setState(key, state, storage){
            pending = { key, state, storage }

            if(timer) clearTimeout(timer)
            timer = setTimeout(flush, 250)
        },

        //drop a queued write without performing it - used when the persisted key is being
        //deliberately cleared (logout / master re-import), so a write queued moments earlier
        //cannot land afterwards and resurrect the state that was just thrown away
        cancel(){
            if(timer){
                clearTimeout(timer)
                timer = null
            }

            pending = null
        },

        flush,
    }
})()

//2026-10-07 jazz, always clear the persisted store through this, never localStorage.removeItem('SRS')
//directly - see cancel() above for why
export const clearPersistedState = () => {
    __persistQueue.cancel()
    localStorage.removeItem('SRS')
}

export default new Vuex.Store({
    plugins: [
        createPersistedState({
            key: 'SRS',
            storage: localStorage,
            setState: __persistQueue.setState
        })
    ],          
    
    state: {
        UserInfo: {
            UserName : '',
            FullName : '',
            isAdmin  : '0',
            isLogged : false,
        },
        PlanInfo: {
            ConstructionCode: '',
            RevisionNumber  : '',
            RequestNumber   : '',
            SiyoCode        : '',
            HRDSQL2SiyoCode : '',
            HouseType       : '',
            SelectedItems   : [],
            JibanInfo: {
                jibanList: [], 
                selectedJibanIdBranch: {
                    Address       : null,
                    SequentialNo  :null,
                    Decision      :null,
                    DecisionStatus: null
                }
            },
            isPrevious      : false,
            PreviousSentDate: '',
            CurrentMenu     : null,
            PreviousId      : null,
            CopyPlan        : false,
            CopyPlanInfo: {
                ConstructionCode: '',
                RevisionNumber  : '',
                RequestNumber   : '',
            },
            EditKey         : '',
            isShiyoFetched  : false,
            idForCompletion : null,
        },
        Referrer: 'SRS',  
        Master: {
            HouseTypes   : [],
            MenuItems    : [],
            SheetItems   : [],
            UIVersion    : '' 
        },     
        UserInterface: {
            CompletionData               : [],
            // SelectedWhenActiveSystemBath : null,
            SelectedWhenActiveToiletFixtures: [],
            // SelectedWhenActiveToilet1F   : [],
            // SelectedWhenActiveToilet2F   : null,
            // SelectedWhenActiveToilet3F   : null,
            SheetItems                   : [],
            GaibuList                    : [],
            SetsubiSheet                 : [],
            BasicSpecs                   : [],
            GaibuListLoading             : false, //2026-09-28 jazz, background load flag while BasicSpecs is already shown
            SetsubiSheetLoading          : false, //2026-09-28 jazz, background load flag while BasicSpecs is already shown
            ShiyouData                   : [],
            isViewDisabled               : false,
            isPreselectedSet             : false,
            ReUpdate                     : false,
            isAllowMenu                  : false,
            // Referrer                     : 'SRS',
            isCategoryOnly               : false,
            isRunSaving                  : false,
            IsSheetItemsCountModified    : false,
            SocketURI                    : null //2025-09-02 jazz
        },
        BypassOn: false
    },

    actions:{
        //2025-12-03 jazz
        async BypassOn( context, payload ){
            await context.commit( 'BypassOn', payload )
        },

        //2025-09-02 jazz
        async SaveSocketURI( context, payload ){
            await context.commit( 'SaveSocketURI', payload )
        },

        async AddCurrentShioCode( context, payload ){
            await context.commit( 'AddCurrentShioCode', payload )
        },

        async AddHRDSQL2ShioCode( context, payload ){
            await context.commit( 'AddHRDSQL2ShioCode', payload )
        },

        async ShowChildItems( context, payload ){
            await context.commit( 'ShowChildItems', payload )
        },

        async Referrer( context, payload ){
            await context.commit( 'Referrer', payload )
        },

        async LoadPlanFromShiyousho( context, payload ){
            await context.commit( 'LoadPlanFromShiyousho', payload )
        },

        //2025-08-08 jazz
        async SelectedWhenActiveToiletFixtures ( context, payload ){
            await context.commit( 'SelectedWhenActiveToiletFixtures', payload )
        },

        // async SelectedWhenActiveSystemBath( context, payload ){
        //     await context.commit( 'SelectedWhenActiveSystemBath', payload )
        // },

        // async SelectedWhenActiveToilet1F ( context, payload ){
        //     await context.commit( 'SelectedWhenActiveToilet1F', payload )
        // },
 
        // async SelectedWhenActiveToilet2F ( context, payload ){
        //     await context.commit( 'SelectedWhenActiveToilet2F', payload )
        // },

        // async SelectedWhenActiveToilet3F ( context, payload ){
        //     await context.commit( 'SelectedWhenActiveToilet3F', payload )
        // },

        async IsSheetItemsCountModified( context, payload ){
            await context.commit( 'IsSheetItemsCountModified', payload )
        },

        async UserSheetItems( context, payload ){
            await context.commit( 'UserSheetItems', payload )
        },

        async isCategoryOnly( context, payload ){
            await context.commit( 'isCategoryOnly', payload )
        },

        async isRunSaving( context, payload ){
            await context.commit( 'isRunSaving', payload )
        },

        async isLogged( context, payload ){
            await context.commit( 'isLogged', payload )
        },

        async previousSentDate( context, payload ){
            await context.commit( 'previousSentDate', payload )
        },

        async CurrentMenu( context, payload ){
            await context.commit('CurrentMenu', payload)
        },

        async sheetItems( context, payload ){
            await context.commit( 'sheetItems', payload )
        },

        async updateSheetItems( context, payload ){
            await context.commit( 'updateSheetItems', payload )
        },

        async toggleShow( context, payload ){
            await context.commit( 'toggleShow', payload )
        },

        //2025-08-22 jazz
        // async toggleCompleted( context, payload ){
        //     await context.commit( 'toggleCompleted', payload )
        // },  

        async isAllowMenu( context, payload ){
            await context.commit( 'isAllowMenu', payload )
        },

        //Mae added 2022.05.19
        async UpdateUserInterfaceItem( context, payload ){
            await context.commit( 'UpdateUserInterfaceItem', payload )
        },

        async isViewDisabled( context, payload ){
          await context.commit( 'isViewDisabled', payload )
        },

        async MenuItems( context, payload ){
            await context.commit( 'MenuItems', payload )
        },

        async UpdateSelectedItem( context, payload ){
            await context.commit( 'UpdateSelectedItem', payload )
        },

        async CopyPlan( context, payload ){
            await context.commit( 'CopyPlan', payload )
        },

        async SetPreviousId( context, payload ){
            await context.commit( 'SetPreviousId', payload )
        },

        async ReUpdate( context, payload ){
            await context.commit( 'ReUpdate', payload )
        },   

        async SelectedItems( context, payload ){
            await context.commit( 'SelectedItems', payload )
        },

        async SetPreselected( context ){
            await context.commit( 'SetPreselected' )
        },

        async UpdateInterfaceItem( context, payload ){
            await context.commit( 'UpdateInterfaceItem', payload )
        },

        async UpdatePlanInfoItem( context, payload ){
            await context.commit( 'UpdatePlanInfoItem', payload )
        },

        async AddToSelectedItems( context, payload ){
            await context.commit( 'AddToSelectedItems', payload )
        },

        //2026-10-07 jazz, bulk form of AddToSelectedItems - see the mutation for why
        async AddManyToSelectedItems( context, payload ){
            await context.commit( 'AddManyToSelectedItems', payload )
        },

        async UserSetsubiSheet( context, payload ){
            await context.commit( 'UserSetsubiSheet', payload )
        },

        async UserGaibuList( context, payload ){
            await context.commit( 'UserGaibuList', payload )
        },

        async UserBasicSpecs( context, payload ){
            await context.commit( 'UserBasicSpecs', payload )
        },

        async AddUserBasicSpecs( context, payload ){
            await context.commit( 'AddUserBasicSpecs', payload )
        },

        async AddUserSetsubiSheet( context, payload ){
            await context.commit( 'AddUserSetsubiSheet', payload )
        },

        async AddUserGaibuList( context, payload){
            await context.commit( 'AddUserGaibuList', payload )
        },

        async DeleteItem( context, payload ){
            await context.commit( 'deleteItem', payload )
        },

        async RemoveUserGaibuList( context, payload ){
            await context.commit( 'RemoveUserGaibuList', payload )
        },
        
        async RemoveUserBasicSpecs( context, payload ){
            await context.commit( 'RemoveUserBasicSpecs', payload )
        },

        async RemoveUserSetsubiSheet( context, payload ){
            await context.commit( 'RemoveUserSetsubiSheet', payload )
        },

        async setUIVersion( context, payload ){
            await context.commit( 'setUIVersion', payload )
        },

        async setUserInfo( context, payload ) {
            await context.commit( 'setUserInfo', payload )
        },

        async resetUserInfo( context ){
            await context.commit( 'resetUserInfo' )
        },

        async isAdmin( context, payload ) {
            await context.commit( 'isAdmin', payload )
        },

        async selectedItemsPrevious( context, payload ) {
            await context.commit( 'selectedItemsPrevious', payload )
        },

        async JibanInfo( context, payload ) {
            await context.commit( 'JibanInfo', payload )
        },

        async UpdateJibanSelected( context, payload ) {
            await context.commit( 'UpdateJibanSelected', payload )
        },
 
        async setPlanInfo( context, payload ){
            await context.commit( 'setPlanInfo', payload )
        },

        async savePlanInfo( context, payload ){
            await context.commit( 'savePlanInfo', payload )
        },

        async isPrevious( context, payload ){
            await context.commit( 'isPrevious', payload )
        },

        async EditKey( context, payload ){
            await context.commit( 'EditKey', payload )
        },

        async isShiyoFetched( context, payload ){
            await context.commit( 'isShiyoFetched', payload ) 
        },

        async resetUserInterface( context ){
            await context.commit( 'resetUserInterface' )
        },

        //2026-09-28 jazz, toggles UserInterface[<sheet>Loading] while that sheet imports in the background
        async SheetLoading( context, payload ){
            await context.commit( 'SheetLoading', payload )
        },

        async SelectedItems( context, payload ){
            await context.commit( 'SelectedItems', payload ) 
        },

        async RemoveSelectedItem( context, payload){
            await context.commit( 'RemoveSelectedItem', payload ) 
        }, 

        async copyPlanInfo( context, payload ){
          await context.commit( 'copyPlanInfo', payload ) 
        },
    },

    mutations: {
        //2025-12-03 jazz
        BypassOn( state, payload ){
            state.BypassOn = payload;
        },

        //2025-09-02 jazz
        SaveSocketURI( state, payload ){
            state.UserInterface.SocketURI = payload;
        },

        AddHRDSQL2ShioCode( state, payload ){
            state.PlanInfo.HRDSQL2SiyoCode = payload;
        },

        AddCurrentShioCode( state, payload ){
            state.PlanInfo.SiyoCode = payload;
        },

        ShowChildItems( state, payload ){
            if( payload.iscategory ){
                      if(!state.UserInterface[payload.sheet][payload.index]['category'][payload.categoryindex]) return;//it is temporary only. Need to revise (added jazz 2025-03-06)
                
                if(payload.value != state.UserInterface[payload.sheet][payload.index]['category'][payload.categoryindex]['isinterfacedefault']){
                    state.UserInterface[payload.sheet][payload.index]['category'][payload.categoryindex]['isinterfacedefault'] = payload.value

                    if(payload.value == 1){
                        state.UserInterface[payload.sheet][payload.index].needtocompletecount++

                        // for (var i = state.PlanInfo.SelectedItems.length - 1; i >= 0; i--) {
                        //   if(state.UserInterface[payload.sheet][payload.index].items !== state.PlanInfo.SelectedItems[i].MainCategoryName){ continue };
                        //   if(state.UserInterface[payload.sheet][payload.index]['category'][payload.categoryindex].id !== state.PlanInfo.SelectedItems[i].CategoryId){ continue };

                        //   let tmpSelectedItem = {
                        //       MainCategoryName: state.PlanInfo.SelectedItems[i].MainCategoryName,
                        //       CategoryName: state.PlanInfo.SelectedItems[i].CategoryName,
                        //       SubcategoryName: state.PlanInfo.SelectedItems[i].SubcategoryName,
                        //       SpecsName: state.PlanInfo.SelectedItems[i].SpecsName,
                        //       TextureName: state.PlanInfo.SelectedItems[i].TextureName
                        //   }

                        //    bus.$emit(`UpdatePreselectedTexture`, tmpSelectedItem)

                        // }
                    }else{
                        state.UserInterface[payload.sheet][payload.index].needtocompletecount--

                        for (var i = state.PlanInfo.SelectedItems.length - 1; i >= 0; i--) {
                              if(state.UserInterface[payload.sheet][payload.index].items !== state.PlanInfo.SelectedItems[i].MainCategoryName){ continue };
                              if(state.UserInterface[payload.sheet][payload.index]['category'][payload.categoryindex].id !== state.PlanInfo.SelectedItems[i].CategoryId){ continue };

                              state.PlanInfo.SelectedItems.splice(i, 1)
                        }
                    }
                }
            }
        },

        copyPlanInfo( state, payload ){
            state.PlanInfo.CopyPlanInfo = payload
        },

        Referrer( state, payload ){
            state.Referrer = payload
        },

        //2025-08-08 jazz
        SelectedWhenActiveToiletFixtures( state, payload ){
            if(state.UserInterface.SelectedWhenActiveToiletFixtures.length > 0){
                for(let i = 0; i < state.UserInterface.SelectedWhenActiveToiletFixtures.length; i++){

                    if(state.UserInterface.SelectedWhenActiveToiletFixtures[i].seq == payload.seq && 
                        state.UserInterface.SelectedWhenActiveToiletFixtures[i].mainid === payload.mainid
                    ){
                        state.UserInterface.SelectedWhenActiveToiletFixtures.splice(i, 1)
                    }
                }
            }

            state.UserInterface.SelectedWhenActiveToiletFixtures.push(payload)
        },

        // SelectedWhenActiveSystemBath( state, payload ){
        //   state.UserInterface.SelectedWhenActiveSystemBath = payload
        // },
 
        // SelectedWhenActiveToilet1F( state, payload ){
        //     if(state.UserInterface.SelectedWhenActiveToilet1F.length > 0){
        //         for(let i = 0; i < state.UserInterface.SelectedWhenActiveToilet1F.length; i++){
        //             if(state.UserInterface.SelectedWhenActiveToilet1F[i].seq === payload.seq){
        //                 state.UserInterface.SelectedWhenActiveToilet1F.splice(i, 1)
        //             }
        //         }
        //     }

        //     state.UserInterface.SelectedWhenActiveToilet1F.push(payload)
        // },

        // SelectedWhenActiveToilet2F( state, payload ){
        //     state.UserInterface.SelectedWhenActiveToilet2F = payload
        // },

        // SelectedWhenActiveToilet3F( state, payload ){
        //     state.UserInterface.SelectedWhenActiveToilet3F = payload
        // },

        IsSheetItemsCountModified( state, payload ){
            state.UserInterface.IsSheetItemsCountModified = payload
        },

        isCategoryOnly( state, payload ){
            state.UserInterface.isCategoryOnly = payload
        },

        isRunSaving( state, payload ){
            state.UserInterface.isRunSaving = payload
        },

        UserSheetItems( state, payload ){
            state.UserInterface.SheetItems = null
            state.UserInterface.SheetItems = payload
        },

        LoadPlanFromShiyousho( state, payload ){
            const SETSUBI_MASTER_ID  = 2
            // const SYSTEM_BATH_ID     = 136
            // const TOILET_1F_ID       = 20
            // const TOILET_2F_ID       = 173
            // const TOILET_3F_ID       = 452

            //2025-05-21 jazz
            const SystemBathToiletItems = [
                { id: 136, name: 'SystemBath' },
                { id: 20,  name: 'Toilet1F' },
                { id: 173, name: 'Toilet2F' },
                { id: 452, name: 'Toilet3F' }
            ]
   
            const TOILET_STORAGE_IDS = [4759, 4764, 4823]

            state.PlanInfo.SelectedItems = payload.PlanInfo.SelectedItems

            if( payload.progressBar ){
              payload.progressBar.increment()
            }

            for ( var i = payload.PlanInfo.SelectedItems.length - 1; i >= 0; i-- ) {
                if(payload.PlanInfo.SelectedItems[i].MasterId !== SETSUBI_MASTER_ID) continue;

                if(TOILET_STORAGE_IDS.includes(payload.PlanInfo.SelectedItems[i].CategoryId)) continue;

                // if(payload.PlanInfo.SelectedItems[i].MainCategoryId === SYSTEM_BATH_ID){
                //   state.UserInterface.SelectedWhenActiveSystemBath = payload.PlanInfo.SelectedItems[i].CategoryId
                // }

                // if(payload.PlanInfo.SelectedItems[i].MainCategoryId === TOILET_1F_ID){
                //   state.UserInterface.SelectedWhenActiveToilet1F = payload.PlanInfo.SelectedItems[i].CategoryId
                // }

                // if(payload.PlanInfo.SelectedItems[i].MainCategoryId === TOILET_2F_ID){
                //   state.UserInterface.SelectedWhenActiveToilet2F = payload.PlanInfo.SelectedItems[i].CategoryId
                // }

                // if(payload.PlanInfo.SelectedItems[i].MainCategoryId === TOILET_3F_ID){
                //   state.UserInterface.SelectedWhenActiveToilet3F = payload.PlanInfo.SelectedItems[i].CategoryId
                // }

                //2025-05-21 jazz, refactor
                if( !SystemBathToiletItems.map( item => item.id).includes(payload.PlanInfo.SelectedItems[i].MainCategoryId) ) continue;

                for (let j = SystemBathToiletItems.length - 1; j >= 0; j--) {
                    if( SystemBathToiletItems[j].id != payload.PlanInfo.SelectedItems[i].CategoryId ) continue; 

                    state.UserInterface[`SelectedWhenActive${name}`] = payload.PlanInfo.SelectedItems[i].CategoryId   
                }
            }

            let JibanData = payload.PlanInfo.JibanInfo;

            if(JibanData instanceof Array){
                JibanData = JibanData[0];
            }

            state.PlanInfo.JibanInfo = JibanData

            if(payload.progressBar){
              payload.progressBar.increment();
            }
        },

        previousSentDate( state, payload ){
            state.PlanInfo.PreviousSentDate = payload
        },

        CurrentMenu( state, payload ){
            state.PlanInfo.CurrentMenu = payload
        },

        isLogged( state, payload ){
            state.UserInfo.isLogged = payload
        },
 
        sheetItems( state, payload ){
            state.Master.SheetItems = payload
            state.Master.SheetItems = Object.freeze(state.Master.SheetItems) //2025-07-30 jazz, freeze object
        },

        updateSheetItems( state, payload ){
            state.UserInterface.SheetItems.forEach(item =>{
                if(item.id === payload.id){
                  item.class = 'deep-orange lighten-3'
                }else{
                  item.class = null
                }
            })
        },

        toggleShow( state, payload ){
            const masterArr = ['GaibuList', 'SetsubiSheet', 'BasicSpecs'] //if have new master add in array
            const masterLength = state.UserInterface[masterArr[payload.masterId - 1]].length

            for( let i = 0; i < masterLength; i++ ){
                if( payload.itemName === state.UserInterface[masterArr[payload.masterId - 1]][i].items ){
                   state.UserInterface[masterArr[payload.masterId - 1]][i].isdefaultshown = payload.isshown
                }
            }
        },

        isAllowMenu( state, payload ){
            state.UserInterface.isAllowMenu = payload
        },

        UpdateUserInterfaceItem( state, payload ){
            state.UserInterface[payload.sheet] = payload.newvalue  
        },

        //2025-08-22 jazz
        // removeItemDataItems( state, payload ){
        //     state.PlanInfo.ItemData[payload.maincategoryindex].items.splice(payload.itemindex, 1)
        // },

        RemoveSelectedItem(state, payload){
            //2025-05-15 jazz, to exclude toiletstorage from unchecking
            if( !state.PlanInfo.SelectedItems[payload] ) return;
            if( /トイレ収納|トイレ手摺/.test(state.PlanInfo.SelectedItems[payload].CategoryName) ) return;
   
            state.PlanInfo.SelectedItems.splice(payload, 1)
        },

        removeItemData( state, payload ){
            state.PlanInfo.ItemData.splice(payload, 1)
        },

        //2025-08-22 jazz
        // modifyItemData( state, payload ){
        //     let selectedItems = state.PlanInfo.ItemData
        //     // const currentItems = payload.items
            
        //     // let tmpObj = {}
        //     // tmpObj.ismodified = payload.ismodified
        //     // tmpObj.items = payload.items
        //     // tmpObj.maincategory = payload.maincategory
        //     // tmpObj.masterid = payload.masterid

        //     const itemIndex = selectedItems.map(e => e.maincategory).indexOf(payload.maincategory)

        //     // // //const parentid = ((selectedItems[itemIndex].parentid) ? itemArr.parentid:itemArr.folderid)

        //     if( itemIndex == -1 ){
        //         selectedItems.push(payload)
        //     } else {
        //     //   //   if(payload.clickeditem.isparentcheckbox  == 0 && payload.clickeditem.groupid == -1){
        //     //      //    for(let i = 0; i < selectedItems[itemIndex].items.length; i++){

        //     //        //   console.log( selectedItems[itemIndex].items[i].ParentId + '===' + tmpObj.items[0].ParentId )


        //     //         //   if(selectedItems[itemIndex].items[i].ParentId == tmpObj.items[0].ParentId  ){
        //     //            //   selectedItems[itemIndex].items.splice(i, 1)  

        //     //           // }

        //     //         // }


        //     //           //console.log(payload.clickeditem.isparentcheckbox)
        //     //           //selectedItems[itemIndex].items.push(tmpObj)

        //     //          // selectedItems[itemIndex].items.push(...tmpObj.items)

        //     //     // }else{
    
        //         selectedItems[itemIndex].items = payload.items
        //     }
   
        //     // //     for( let i = 0; i < selectedItems[itemIndex].items.length; i++){
        //     // //         currentItems.forEach(item =>{

        //     // //            // console.log(item.GroupId + '----' + selectedItems[itemIndex].items[i].GroupId)

        //     // //             // if(item.SelectionId == selectedItems[itemIndex].items[i].SelectionId){
        //     // //             //     selectedItems[itemIndex].items[i] = item
        //     // //             // }

        //     // //             if(item.GroupId == selectedItems[itemIndex].items[i].GroupId){
        //     // //                 selectedItems[itemIndex].items[i] = item
        //     // //             }
        //     // //         })
        //     // //     }  
        //     //  }
        // },
        
        //2025-08-22 jazz 
        // AddPreselected( state, payload ){
        //     //console.log(payload)
        //     if( payload.maincategory ){
        //         state.PlanInfo.ItemData.push( payload )
        //     }
        // }, 

        isViewDisabled( state, payload ){
          state.UserInterface.isViewDisabled = payload
        },

        MenuItems( state, payload ){
            state.Master.MenuItems = []
            state.Master.MenuItems = payload

            //2025-07-30 jazz, freeze object
            state.Master.MenuItems.MenuItems = Object.freeze(state.Master.MenuItems.MenuItems)
        }, 

        UpdateSelectedItem( state, payload ){
            state.PlanInfo.SelectedItems[payload.index] = payload.newvalue
        },

        CopyPlan( state, payload ){
            state.PlanInfo.CopyPlan = payload
        },

        SetPreviousId( state, payload ){
            state.PlanInfo.PreviousId = payload
        }, 

        ReUpdate( state, payload ){
            state.UserInterface.ReUpdate = payload
        }, 

        SetPreselected( state ){
            state.UserInterface.isPreselectedSet = true
        }, 

        UpdateInterfaceItem( state, payload ){
            if( payload.itemToUpdate == 0 ){
                state.UserInterface[payload.sheet][payload.index] = payload.value  
            }else if( payload.itemToUpdate == 3 ){
                state.UserInterface[payload.sheet] = payload.value  
            }else{
                state.UserInterface[payload.sheet][payload.index][payload.itemToUpdate] = payload.value
            }
        },

        UpdatePlanInfoItem( state, payload ){
            if( payload.itemToUpdate == 0 ){
                state.PlanInfo.SelectedItems[payload.index] = payload.value  
            }else{
                state.PlanInfo.SelectedItems[payload.index][payload.itemToUpdate] = payload.value
            }
        },

        setUserInfo( state, payload ){
            state.UserInfo.UserName = payload.userName;
            state.UserInfo.Browser = payload.browser;
            state.UserInfo.FullName = payload.fullName;
        },

        resetUserInfo( state ){
            state.UserInfo.UserName = '';
            state.UserInfo.Browser = '';
            state.UserInfo.FullName = '';
        },

        isAdmin( state, payload ){
            state.UserInfo.isAdmin = payload;
        },    

        setUIVersion( state, payload ){
            state.Master.UIVersion = payload
        },

        // uniqueId(state, payload){
        //   state.PlanInfo.uniqueId = payload;
        // },

        EditKey( state, payload ){
            state.PlanInfo.EditKey = payload;
        },

        isShiyoFetched( state, payload ){
            state.PlanInfo.isShiyoFetched = payload
        },

        isPrevious( state, payload ){
            state.PlanInfo.isPrevious = payload
        },

        setPlanInfo( state, payload ){
            state.PlanInfo = []
            state.PlanInfo = payload
        },

        selectedItemsPrevious( state,payload ){
            state.PlanInfo.SelectedItems = [];
            state.PlanInfo.SelectedItems = payload;
        },

        JibanInfo( state,payload ){
            if( payload.selectedJibanIdBranch.Decision ){
                state.PlanInfo.JibanInfo = payload;  
            }else{
                state.PlanInfo.JibanInfo = { jibanList: [], selectedJibanIdBranch: {Address: null, SequentialNo:null, Decision:null, DecisionStatus: null} }
            }
        },

        UpdateJibanSelected( state,payload ){
            if( payload ){
                const jibanSequentialNo = (payload.texturename.split(':')[0]).trim();
                const jibanDecision = (payload.texturename.split(':')[1]).trim();
                state.PlanInfo.JibanInfo.selectedJibanIdBranch.DecisionStatus = jibanDecision
                state.PlanInfo.JibanInfo.selectedJibanIdBranch.SequentialNo = jibanSequentialNo
            }
        },

        UserGaibuList( state, payload ){
            state.UserInterface.GaibuList.push(...payload);
            //state.UserInterface.GaibuList.sort((a, b) => (a.sortno > b.sortno) ? 1 : (a.sortno === b.sortno) ? ((a.items > b.items) ? 1 : -1) : -1 )
        },

        UserSetsubiSheet( state, payload ){
            state.UserInterface.SetsubiSheet.push(...payload);
            //state.UserInterface.SetsubiSheet.sort((a, b) => (a.sortno > b.sortno) ? 1 : (a.sortno === b.sortno) ? ((a.items > b.items) ? 1 : -1) : -1 )
        },

        UserBasicSpecs( state, payload ){
            state.UserInterface.BasicSpecs.push(...payload);
            //state.UserInterface.BasicSpecs.sort((a, b) => (a.sortno > b.sortno) ? 1 : (a.sortno === b.sortno) ? ((a.items > b.items) ? 1 : -1) : -1 )
        },

        //2025-04-28 jazz, multiple items in category
        AddUserSetsubiSheet(state, payload){
            this.dispatch('IsSheetItemsCountModified', true) //needed to update checkbox

            const index = state.UserInterface.SetsubiSheet.findIndex( e => e.items == payload.items && e.id == payload.id );

            if ( index == -1 ) {
                if( !Array.isArray(payload) ){
                    state.UserInterface.SetsubiSheet.push(payload);
                    // state.UserInterface.SetsubiSheet.sort((a, b) => (a.sortno > b.sortno) ? 1 : (a.sortno === b.sortno) ? ((a.items > b.items) ? 1 : -1) : -1 )
                    generateSortFn( state.UserInterface.SetsubiSheet ) //2025-05-08 jazz
                }else{
                    const idx = state.UserInterface.SetsubiSheet.findIndex(item => item.items == payload[0].parentname)

                    state.UserInterface.SetsubiSheet[idx].category = payload
                    generateSortFn( state.UserInterface.SetsubiSheet ) //2025-05-08 jazz
                    // categoryArr.sort((a, b) => (a.sortno > b.sortno) ? 1 : (a.sortno === b.sortno) ? ((a.items > b.items) ? 1 : -1) : -1 )
   
                }
            }
        },

        AddUserBasicSpecs( state, payload ){
            const index = state.UserInterface.BasicSpecs.findIndex( e => e.items == payload.items );

            if (index == -1) {
                state.UserInterface.BasicSpecs.push(payload);
                state.UserInterface.BasicSpecs.sort((a, b) => (a.sortno > b.sortno) ? 1 : (a.sortno === b.sortno) ? ((a.items > b.items) ? 1 : -1) : -1 )      
            }
        },

        AddUserGaibuList( state, payload ){
            const index = state.UserInterface.GaibuList.findIndex( e => e.items == payload.items );

            if (index == -1) {
                state.UserInterface.GaibuList.push(payload); 
                //state.UserInterface.GaibuList.sort((a, b) => (a.sortno > b.sortno) ? 1 : (a.sortno === b.sortno) ? ((a.items > b.items) ? 1 : -1) : -1 ) 
   
                generateSortFn( state.UserInterface.GaibuList ) //2025-05-08 jazz
            }
        },

        //2025-04-29 jazz, multiple item category
        RemoveUserSetsubiSheet(state, payload){
            if( typeof payload === 'object' ){
                let category = (state.UserInterface.SetsubiSheet.filter( item => item.items === payload.parentname ))[0].category
   
                for( let i = 0; i < category.length; i++ ){
                    if( category[i].items === payload.items ){
                        category[i].isinterfacedefault = 0;

                        if( Array.isArray(category[i].subcategory) ){
                            category[i].subcategory.forEach( subcategory =>{
                                subcategory.isinterfacedefault = 0;

                                if( Array.isArray(subcategory.textureList) ){
                                    subcategory.textureList.forEach( texture =>{
                                        texture.isinterfacedefault = 0;
                                    })
                                }
                            })
                        }
                    }
                }

                //remove in selected
                const idx = state.PlanInfo.SelectedItems.findIndex( item => item.MainCategoryName === payload.parentname && item.CategorySelectionId == payload.selectionid )
   
                state.PlanInfo.SelectedItems.splice(idx, 1)
                generateSortFn( state.UserInterface.SetsubiSheet ) //2025-05-08 jazz
            }else{
                state.UserInterface.SetsubiSheet.splice(payload, 1);
                //state.UserInterface.SetsubiSheet.sort((a, b) => (a.sortno > b.sortno) ? 1 : (a.sortno === b.sortno) ? ((a.items > b.items) ? 1 : -1) : -1 )

                generateSortFn( state.UserInterface.SetsubiSheet ) //2025-05-08 jazz
            }
        },

        RemoveUserBasicSpecs( state, payload ){
            state.UserInterface.BasicSpecs.splice(payload, 1);
            state.UserInterface.BasicSpecs.sort((a, b) => (a.sortno > b.sortno) ? 1 : (a.sortno === b.sortno) ? ((a.items > b.items) ? 1 : -1) : -1 )
        },

        RemoveUserGaibuList( state, payload ){
            state.UserInterface.GaibuList.splice(payload, 1);
            // state.UserInterface.GaibuList.sort((a, b) => (a.sortno > b.sortno) ? 1 : (a.sortno === b.sortno) ? ((a.items > b.items) ? 1 : -1) : -1 )
            generateSortFn( state.UserInterface.GaibuList ) //2025-05-08 jazz
        },

        SelectedItems(state, payload){
            state.PlanInfo.SelectedItems = payload

            // let tmpArr = []

            // for (let i = 0; i < state.PlanInfo.SelectedItems.length; i++){
            //   payload.forEach(payloaditem =>{
            //     let expression = `^${payloaditem.MainCategoryId}${payloaditem.CategoryId}`
            //     let re = new RegExp(expression)

            //     if(payloaditem.MainCategoryName == state.PlanInfo.SelectedItems[i].MainCategoryName && payloaditem.Modified){

            //       if(re.test(state.PlanInfo.SelectedItems[i].RowIndex) && payloaditem.SubcategoryId != state.PlanInfo.SelectedItems[i].SubcategoryId  ){
            //         tmpArr.push( state.PlanInfo.SelectedItems.map(e => e.RowIndex).indexOf(state.PlanInfo.SelectedItems[i].RowIndex) )
            //       }
            //     }
            //   })
            // }

            // if(tmpArr.length > 0){
            //   const lastElement = state.PlanInfo.SelectedItems[tmpArr[tmpArr.length - 1] ]  
            //   const tmpItems = state.PlanInfo.SelectedItems.filter(item => item.SubcategoryId != lastElement.SubcategoryId && item.MainCategoryName == lastElement.MainCategoryName   )
              
            //   tmpItems.forEach(item =>{   
            //     if(item.SpecsId > 0){
            //       this.commit('deleteItem',  state.PlanInfo.SelectedItems.map(e => e.RowIndex).indexOf(item.RowIndex) )
            //     }  

            //   })
            // }
        },

        AddToSelectedItems( state, payload ){
            state.PlanInfo.SelectedItems.push(payload)
        },

        //2026-10-07 jazz, bulk form of AddToSelectedItems. createPersistedState is configured with no
        //`paths` / `reducer` (see the plugins block at the top of this file), so EVERY committed mutation
        //re-serialises the whole store - all three sheet trees (GaibuList / SetsubiSheet / BasicSpecs),
        //ShiyouData and Master - and writes it to localStorage synchronously on the main thread. Adding N
        //preselected rows through N separate mutations therefore cost N full-store writes, which is what
        //made checking an item with many defaults (e.g. 1F toilet) visibly slow. Same pushes, one commit,
        //so the store is serialised once.
        AddManyToSelectedItems( state, payload ){
            if( !payload || payload.length === 0 ) return;

            //a loop rather than push(...payload): spreading a large array as arguments can exceed the
            //engine's argument-count limit
            for( let i = 0; i < payload.length; i++ ){
                state.PlanInfo.SelectedItems.push(payload[i])
            }
        }, 

        HouseTypes( state, payload ){
            state.Master.HouseTypes = []
            state.Master.HouseTypes.push(...payload);
            state.Master.HouseTypes = Object.freeze(state.Master.HouseTypes); //2025-07-30 jazz, freeze object to prevent reactivity
        },

        savePlanInfo( state, payload ){
            state.PlanInfo.ConstructionCode = payload.ConstructionCode,
            state.PlanInfo.RevisionNumber   = payload.RevisionNumber,
            state.PlanInfo.RequestNumber    = payload.RequestNumber+'',
            state.PlanInfo.HouseType        = payload.HouseType
        },

        deleteItem( state, payload ){
            if( Array.isArray(payload) ){
              for (var i = payload.length - 1; i >= 0; i--) {
                  state.PlanInfo.SelectedItems.splice(payload[i], 1)
              }
            }

            // state.PlanInfo.SelectedItems.splice(payload, 1)
        },

        resetPlanInfo( state ){
            state.PlanInfo.ConstructionCode = '',
            state.PlanInfo.RevisionNumber   = '',
            state.PlanInfo.RequestNumber    = '',
            state.PlanInfo.SiyoCode         = 0, //2025-03-13
            state.PlanInfo.HRDSQL2SiyoCode  = '', //2025-07-11
            state.PlanInfo.HouseType        = '',
            state.PlanInfo.PreviousId       = null,
            state.PlanInfo.isPrevious       = false,
            state.PlanInfo.CopyPlan         = false,
            state.PlanInfo.SelectedItems    = [],
            state.PlanInfo.idForCompletion  = null,
            state.PlanInfo.JibanInfo        = { jibanList: [], selectedJibanIdBranch: {} }
            state.PlanInfo.CopyPlanInfo     = { ConstructionCode: '', RevisionNumber: '', RequestNumber: ''}
            state.PlanInfo.PreviousSentDate = '',
            state.Master.SheetItems.forEach(item =>{
                item.class = null
            })
            state.UserInterface.isCategoryOnly = false
                state.UserInterface.isRunSaving    = false
        },

        //2026-09-28 jazz, payload: { sheet: 'GaibuList'|'SetsubiSheet', value: boolean }
        SheetLoading( state, payload ){
            state.UserInterface[payload.sheet + 'Loading'] = payload.value
        },

        resetUserInterface( state ){
            state.UserInterface.GaibuList        = []
            state.UserInterface.SetsubiSheet     = []
            state.UserInterface.BasicSpecs       = []
            //2026-09-29 jazz, default to true (tab disabled) on reset - GaibuList/SetsubiSheet aren't
            //loaded yet at this point regardless of which flow is starting. Every flow that actually loads
            //a plan (SpawnConvertWebWorkers/LoadSheetFromWorkerData in SRSFunctions.js, and
            //newplan.vue's loadRemainingSheetsInBackground) always flips this back to false in a finally
            //block once done, so this can't get stuck. Previously defaulting to false left a real gap:
            //ExistingPlan.vue's copy-plan flow sets PlanInfo.ConstructionCode (the *other* disable
            //condition on these tabs) before calling resetUserInterface, so between reset and the
            //background load actually starting, the tabs were enabled/clickable despite having no data.
            state.UserInterface.GaibuListLoading    = true
            state.UserInterface.SetsubiSheetLoading = true
            // state.UserInterface.HiddenItems   = [] 2025-05-21 jazz
            state.UserInterface.isPreselectedSet = false
            state.UserInterface.isAllowMenu      = false
            // state.UserInterface.isTimedOut    = false
            state.UserInterface.IsSheetItemsCountModified    = false
            // state.UserInterface.SelectedWhenActiveSystemBath = null
            state.UserInterface.SelectedWhenActiveToiletFixtures   = [] //2025-08-08 jazz
            // state.UserInterface.SelectedWhenActiveToilet1F   = []
            // state.UserInterface.SelectedWhenActiveToilet2F   = null
            // state.UserInterface.SelectedWhenActiveToilet3F   = null
            state.UserInterface.CompletionData               = []
        },

        // selectedInterface(state, payload){
        //   //state.UserInterface.RequestSheets = payload;
        // }
    }
});